import { Box, Button, Switch, useTheme } from "@mui/material";
import { useEffect, useState } from "react";
import { WalletWatchlistService } from "../services/wallet-watchlist.service";
import { WalletItem } from "../models/wallet-watchlist.models";
import PageBanner from "../components/application/PageBanner";
import { ImageService } from "../services/image.service";
import { Icons, Images } from "../models/assets-files";
import {
  BannerTexts,
  DataGridText,
  DialogText,
  WalletWatchlistText,
} from "../data/texts";
import AddToPhotosOutlinedIcon from "@mui/icons-material/AddToPhotosOutlined";
import CustomTable, { TableRow } from "../components/common/CustomTable";
import { GridColDef } from "@mui/x-data-grid";
import { DateTimeService } from "../services/date-time.service";
import Image from "../components/common/Image";
import ConfirmationDialog from "../components/common/CustomDialog";
import { getConfirmButtons } from "../data/custom-dialog-templates";
import AddWalletDialog, {
  AddWalletFormData,
} from "../components/wallet-watchlist/AddWalletDialog";
import { DialogCta } from "../models/custom-dialog.models";

const WalletWatchlistPage = () => {
  const [watchList, setWatchList] = useState<WalletItem[]>();
  const [tableRows, setTableRows] = useState<TableRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [watchItemToDelete, setWatchItemToDelete] =
    useState<WalletItem | null>();
  const [totalWatchItems, setTotalWatchItems] = useState(0);

  const tableColumns: GridColDef[] = [
    {
      field: "id",
      headerName: "ID",
      width: 90,
      cellClassName: "id-column",
    },
    {
      flex: 1,
      field: "type",
      headerName: "Type",
      minWidth: 100,
    },
    {
      flex: 3,
      minWidth: 400,
      field: "address",
      headerName: "Address",
    },
    {
      flex: 1,
      field: "createdAt",
      headerName: "Created At",
      minWidth: 100,
    },
    {
      flex: 1,
      field: "native",
      minWidth: 100,
      headerName: "Native",
    },
    {
      flex: 1,
      minWidth: 100,
      field: "usdt",
      headerName: "USDT",
    },
    {
      flex: 1,
      minWidth: 150,
      field: "watchStatus",
      headerName: "Watch Status",
      renderCell: (param) => (
        <Switch
          onChange={() => onSwitchStatus(param.row)}
          checked={param.row.watchStatus}
        />
      ),
    },
    {
      flex: 1,
      field: "actions",
      minWidth: 100,
      headerName: "Actions",
      renderCell: (params) => (
        <Box sx={{ ...theme.customStyles.flexStart, height: "100%" }}>
          <Image
            onClick={() => setWatchItemToDelete(params.row.watchItem)}
            sx={{ cursor: "pointer" }}
            src={ImageService.getLocalIconPath(
              Icons.Folders.UPLOADED_FILES_ACTIONS,
              Icons.UploadedFilesActions.DELETE
            )}
          />
        </Box>
      ),
    },
  ];

  const onDeleteWatchItem = async (cta: DialogCta) => {
    if (cta === DialogCta.CANCEL) {
      setWatchItemToDelete(null);
      return;
    }
    if (!watchItemToDelete?.id) return;
    setIsLoading(true);
    setWatchItemToDelete(null);

    await WalletWatchlistService.deleteWalletAddress(watchItemToDelete.id);
    setTableRows((prevItems) =>
      prevItems.filter((item) => item.id !== watchItemToDelete.id)
    );
  };

  const theme = useTheme();

  useEffect(() => {
    fetchWatchlist();
  }, []);

  useEffect(() => {
    if (watchList) {
      initTableRows();
    }
  }, [watchList]);

  useEffect(() => {
    if (watchList) {
      setIsLoading(false);
    }
  }, [tableRows]);

  const onSwitchStatus = async (row: TableRow) => {
    setIsLoading(true);
    await WalletWatchlistService.updateWalletActiveStatus(
      row.id,
      !row.watchStatus
    );
    setTableRows((prevRows) =>
      prevRows.map((currentRow) =>
        currentRow.id === row.id
          ? { ...currentRow, watchStatus: !currentRow.watchStatus }
          : currentRow
      )
    );
  };

  const fetchWatchlist = async () => {
    const response = await WalletWatchlistService.getWalletListRequest();
    setIsLoading(false);
    setWatchList(response);
  };

  const initTableRows = () => {
    if (!watchList) return;
    const rows: TableRow[] = [];
    watchList.forEach((watchItem) => {
      const { id, type, address, createdAt, native, usdt, active } = watchItem;
      const date = DateTimeService.convertDateToDDMMYYYY(new Date(createdAt));

      rows.push({
        id,
        type: type.capitalize(),
        address,
        createdAt: date,
        native,
        usdt,
        watchStatus: active,
        watchItem,
      });
    });
    setTotalWatchItems(rows.length);
    setTableRows(rows);
  };

  const onAddWatchItem = async (formData: AddWalletFormData) => {
    setIsLoading(true);
    setIsAddDialogOpen(false);
    await WalletWatchlistService.addWalletAddressRequest(
      formData.walletType.toLowerCase(),
      formData.walletAddress
    );
    await fetchWatchlist();
  };
  return (
    <Box sx={{ padding: "24px" }}>
      <PageBanner
        imageSrc={ImageService.getLocalImgPath(
          Images.Folders.BANNERS,
          Images.Banners.HARDWARE,
          "png"
        )}
        title={BannerTexts.WALLET_WATCHLIST_TITLE}
        subtitle={BannerTexts.WALLET_WATCHLIST_DESCRIPTION}
      >
        <slot name="content">
          <Button
            onClick={() => setIsAddDialogOpen(true)}
            startIcon={<AddToPhotosOutlinedIcon />}
            sx={{
              background: theme.palette.text.primary,
              color: "white",
              fontSize: "17px",
              paddingInline: "20px",
              paddingBlock: "10px",
              borderRadius: "8px",
            }}
          >
            {WalletWatchlistText.ADD_NEW_WALLET}
          </Button>
        </slot>
      </PageBanner>

      <CustomTable
        isLoading={isLoading}
        totalRowNumber={totalWatchItems}
        tableRows={tableRows}
        tableColumns={tableColumns}
        localeText={DataGridText.NO_WATCHLIST_ITEMS}
      />
      <ConfirmationDialog
        isOpen={!!watchItemToDelete}
        onButton={onDeleteWatchItem}
        title={DialogText.DELETE_WATCH_ITEM_TITLE}
        text={DialogText.DELETE_WATCH_ITEM_TEXT}
        buttons={getConfirmButtons()}
      />
      <AddWalletDialog
        onSubmit={onAddWatchItem}
        onClose={() => setIsAddDialogOpen(false)}
        isOpen={isAddDialogOpen}
      />
    </Box>
  );
};

export default WalletWatchlistPage;
